import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Table,
    Button,
    Space,
    Input,
    Card,
    Drawer,
    notification,
    Row,
    Col,
    Avatar,
    Tag,
    Popconfirm,
    Grid,
    Tooltip
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    SearchOutlined,
    FileTextOutlined,
    EyeOutlined,
    DeleteOutlined,
} from '@ant-design/icons';
import { salesService } from '../../services/salesService';
import SalesForm from './SalesForm';

const { Column } = Table;
const { Search } = Input;

const SalesList = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const navigate = useNavigate();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const [searchText, setSearchText] = useState('');
    const [isDrawerVisible, setIsDrawerVisible] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);

    const fetchInvoices = async (page = 1, search = '') => {
        try {
            setLoading(true);
            const response = await salesService.getAllSalesInvoices({
                page,
                limit: pagination.pageSize,
                search
            });

            setInvoices(Array.isArray(response?.data) ? response.data : []);

            setPagination(prev => ({
                ...prev,
                current: response?.pagination?.page || 1,
                total: response?.pagination?.total || 0
            }));
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to fetch sales invoices'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInvoices();
    }, []);

    const handleTableChange = (pagination) => {
        fetchInvoices(pagination.current, searchText);
    };

    const handleSearch = (value) => {
        setSearchText(value);
        fetchInvoices(1, value);
    };

    const handleAdd = () => {
        setSelectedInvoice(null);
        setIsDrawerVisible(true);
    };

    const handleView = (id) => {
        navigate(`/sales/${id}`);
    };

    const handleDelete = async (id) => {
        try {
            setLoading(true);
            await salesService.deleteSalesInvoice(id);
            notification.success({
                message: 'Deleted',
                description: 'Invoice deleted successfully'
            });
            fetchInvoices(pagination.current, searchText);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: error.response?.data?.error || 'Failed to delete invoice'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDrawerClose = () => {
        setIsDrawerVisible(false);
        setSelectedInvoice(null);
    };

    const handleSuccess = () => {
        handleDrawerClose();
        fetchInvoices(pagination.current, searchText);
    };

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Row justify="space-between" align="middle" gutter={[8, 8]}>
                        <Col xs={24} sm={12}>
                            <Space>
                                <Avatar size={isMobile ? "middle" : "large"} icon={<FileTextOutlined />} style={{ backgroundColor: '#52c41a' }} />
                                <div>
                                    <h3 style={{ margin: 0, fontSize: isMobile ? '16px' : '20px' }}>{isMobile ? 'Sales' : 'Sales Invoices'}</h3>
                                    {!isMobile && <p style={{ margin: 0, color: '#8c8c8c' }}>Manage customer invoices</p>}
                                </div>
                            </Space>
                        </Col>
                        <Col xs={24} sm={12} style={{ textAlign: isMobile ? 'left' : 'right' }}>
                            <Space wrap>
                                <Search
                                    placeholder="Search..."
                                    allowClear
                                    enterButton={<SearchOutlined />}
                                    size={isMobile ? "middle" : "large"}
                                    style={{ width: isMobile ? '160px' : '300px' }}
                                    onSearch={handleSearch}
                                />
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    size={isMobile ? "middle" : "large"}
                                    onClick={handleAdd}
                                >
                                    {isMobile ? "Add" : "New Invoice"}
                                </Button>
                            </Space>
                        </Col>
                    </Row>

                    <Table
                        dataSource={invoices}
                        loading={loading}
                        rowKey="id"
                        pagination={{
                            ...pagination,
                            size: isMobile ? 'small' : 'middle',
                            showSizeChanger: !isMobile
                        }}
                        onChange={handleTableChange}
                        scroll={{ x: 'max-content' }}
                        size={isMobile ? 'small' : 'middle'}
                    >
                        {isMobile && (
                            <Column
                                title="Invoice"
                                key="invoiceMobile"
                                render={(record) => (
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <div style={{ fontWeight: 700 }}>{record.invoiceNumber}</div>
                                        <div style={{ fontSize: '12px' }}>{record.receiver?.customerName}</div>
                                        <div style={{ color: '#52c41a', fontWeight: 600 }}>₹{Math.round(record.totalAfterTax || 0).toLocaleString()}</div>
                                    </div>
                                )}
                            />
                        )}
                        <Column
                            title="Invoice Number"
                            dataIndex="invoiceNumber"
                            key="invoiceNumber"
                            responsive={['md']}
                            width={120}
                            render={(text) => <strong>{text}</strong>}
                        />
                        <Column
                            title="PO Number"
                            dataIndex="poNumber"
                            key="poNumber"
                            responsive={['md']}
                            width={110}
                            render={(text) => text || '-'}
                        />
                        <Column
                            title="Date"
                            dataIndex="invoiceDate"
                            key="invoiceDate"
                            responsive={['md']}
                            width={100}
                            render={(date) => new Date(date).toLocaleDateString()}
                        />
                        <Column
                            title="Customer"
                            dataIndex={['receiver', 'customerName']}
                            key="customerName"
                            responsive={['md']}
                            width={160}
                            ellipsis={true}
                        />
                        <Column
                            title="Issued By"
                            dataIndex={['company', 'companyName']}
                            key="companyName"
                            responsive={['md']}
                            width={200}
                            render={(text) => <Tag color="blue" style={{ whiteSpace: 'normal', height: 'auto' }}>{text}</Tag>}
                        />
                        <Column
                            title="Contract Amt"
                            dataIndex="contractAmount"
                            key="contractAmount"
                            responsive={['md']}
                            width={120}
                            render={(amount) => `₹${Math.round(amount || 0)}`}
                        />
                        <Column
                            title="Total"
                            dataIndex="totalAfterTax"
                            key="totalAfterTax"
                            responsive={['md']}
                            width={120}
                            render={(amount) => (
                                <strong style={{ color: '#52c41a' }}>
                                    ₹{parseFloat(amount || 0).toFixed(2)}
                                </strong>
                            )}
                        />
                        <Column
                            title="Actions"
                            key="actions"
                            fixed={isMobile ? false : "right"}
                            width={isMobile ? 100 : 100}
                            render={(_, record) => (
                                <Space>
                                    <Tooltip title="View">
                                        <Button
                                            type="text"
                                            icon={<EyeOutlined style={{ color: '#52c41a' }} />}
                                            onClick={() => handleView(record.id)}
                                        />
                                    </Tooltip>
                                    <Popconfirm
                                        title="Delete Invoice"
                                        description="Are you sure?"
                                        onConfirm={() => handleDelete(record.id)}
                                        okText="Yes"
                                        cancelText="No"
                                        okButtonProps={{ danger: true, loading: loading }}
                                    >
                                        <Tooltip title="Delete">
                                            <Button
                                                type="text"
                                                danger
                                                icon={<DeleteOutlined />}
                                            />
                                        </Tooltip>
                                    </Popconfirm>
                                </Space>
                            )}
                        />
                    </Table>
                </Space>
            </Card>

            <Drawer
                title="Create Sales Invoice"
                width={isMobile ? '100%' : 900}
                onClose={handleDrawerClose}
                open={isDrawerVisible}
                destroyOnClose
            >
                <SalesForm
                    onSuccess={handleSuccess}
                    onCancel={handleDrawerClose}
                />
            </Drawer>
        </Space>
    );
};

export default SalesList;
